// Sandbox view — Bayesian scenario explorer.
//
// Three columns:
//   - Left rail: topic picker, scenario list (load / save / delete).
//   - Middle: shock dials + weight sliders.
//   - Right: posterior cards (readiness, lens relevances, timing) with fan
//     charts, plus the next-best-experiment callout.
//
// Recomputation is synchronous on every slider 'input' event — the engine
// (lib/sandbox.ts) is closed-form Normal-Normal so this is sub-millisecond.

import {
  ALL_SHOCKS,
  deleteScenario,
  inputsFromTopic,
  listScenarios,
  runSandbox,
  saveScenario,
  SHOCK_HINT,
  SHOCK_LABEL,
  ZERO_SHOCKS,
  type SavedScenario,
  type SandboxResult,
  type ShockKey,
  type Shocks,
} from "../lib/sandbox.ts";
import { LENSES, LENS_LABEL, lensOfBusinessLine, type Lens } from "../lib/macros.ts";
import { composeTopic, loadGraph } from "../lib/queries.ts";
import type { BusinessLine, TopicWithContext } from "../lib/types.ts";

interface State {
  graph: Awaited<ReturnType<typeof loadGraph>> | null;
  ctxs: TopicWithContext[];
  topic: TopicWithContext | null;
  shocks: Shocks;
  weights: Record<string, number>;
  scenarios: SavedScenario[];
}

const READINESS_INPUTS = [
  "research", "funding_yoy", "capital", "institutional",
  "consumer", "regs", "scale", "timing",
];
const READINESS_INPUT_LABELS: Record<string, string> = {
  research:      "Research & patents",
  funding_yoy:   "Funding YoY",
  capital:       "Capital raised",
  institutional: "Institutional adoption",
  consumer:      "Consumer adoption",
  regs:          "Regulatory activity",
  scale:         "Market scale",
  timing:        "Timing momentum",
};

export function renderSandbox(host: HTMLElement): () => void {
  const state: State = {
    graph: null,
    ctxs: [],
    topic: null,
    shocks: { ...ZERO_SHOCKS },
    weights: Object.fromEntries(READINESS_INPUTS.map((k) => [k, 1])),
    scenarios: listScenarios(),
  };
  let cancelled = false;

  host.innerHTML = `<div class="sandbox-loading muted">Loading sandbox…</div>`;

  loadGraph().then((g) => {
    if (cancelled) return;
    state.graph = g;
    state.ctxs = g.topics.map((t) => composeTopic(t.id, g)!).filter(Boolean);
    // Default-pick the first H3 topic if any, otherwise the first.
    state.topic = state.ctxs.find((c) => c.topic.h3_thesis_flag) ?? state.ctxs[0] ?? null;
    paint();
  }).catch((e) => {
    if (cancelled) return;
    host.innerHTML = `<div class="viz-empty">Sandbox load failed: ${escapeHtml(String(e))}</div>`;
  });

  function lensRollup(ctx: TopicWithContext, bls: BusinessLine[]): Record<Lens, number | null> {
    const buckets: Record<Lens, number[]> = { client: [], field: [], enterprise: [] };
    for (const bl of bls) {
      const lens = lensOfBusinessLine(bl);
      const r = ctx.relevance[bl.key];
      if (r?.score != null) buckets[lens].push(r.score);
    }
    return {
      client:     buckets.client.length     ? mean(buckets.client)     : null,
      field:      buckets.field.length      ? mean(buckets.field)      : null,
      enterprise: buckets.enterprise.length ? mean(buckets.enterprise) : null,
    };
  }
  function mean(xs: number[]): number {
    return xs.reduce((a, b) => a + b, 0) / xs.length;
  }

  function compute(): SandboxResult | null {
    if (!state.topic || !state.graph) return null;
    const lens = lensRollup(state.topic, state.graph.businessLines);
    const inputs = inputsFromTopic(state.topic, lens, state.shocks, state.weights);
    return runSandbox(inputs);
  }

  function paint() {
    if (!state.graph || !state.topic) return;
    const result = compute();

    const topicOptions = state.ctxs
      .slice()
      .sort((a, b) => a.topic.name.localeCompare(b.topic.name))
      .map((c) =>
        `<option value="${c.topic.id}" ${state.topic?.topic.id === c.topic.id ? "selected" : ""}>${escapeHtml(c.topic.name)}</option>`,
      )
      .join("");

    const scenarioList = state.scenarios.length
      ? state.scenarios
          .map((s) => `
            <li class="scenario-row" data-id="${s.id}">
              <button class="scenario-load" data-id="${s.id}">
                <span class="scenario-name">${escapeHtml(s.name)}</span>
                <span class="scenario-thesis">${escapeHtml(s.thesis)}</span>
                <span class="scenario-meta">${escapeHtml(s.topic_name)} · ${s.saved_at.slice(0, 10)}</span>
              </button>
              <button class="scenario-del" data-id="${s.id}" title="Delete">×</button>
            </li>`)
          .join("")
      : `<li class="muted scenario-empty">No saved scenarios yet.</li>`;

    const shockSliders = ALL_SHOCKS.map((k) => `
      <div class="shock-slider">
        <div class="shock-slider-head">
          <label class="shock-slider-label">${SHOCK_LABEL[k]}</label>
          <span class="shock-slider-value">${state.shocks[k] >= 0 ? "+" : ""}${state.shocks[k].toFixed(1)}</span>
        </div>
        <input type="range" min="-2" max="2" step="0.1" value="${state.shocks[k]}" data-shock="${k}" class="shock-slider-input" />
        <span class="shock-slider-hint">${SHOCK_HINT[k]}</span>
      </div>
    `).join("");

    const weightSliders = READINESS_INPUTS.map((k) => `
      <div class="weight-slider">
        <label class="weight-slider-label">${READINESS_INPUT_LABELS[k]}</label>
        <input type="range" min="0" max="2" step="0.1" value="${state.weights[k]}" data-weight="${k}" class="weight-slider-input" />
        <span class="weight-slider-value">${state.weights[k].toFixed(1)}×</span>
      </div>
    `).join("");

    const posterior = (label: string, p: SandboxResult["readiness"], scaleMin: number, scaleMax: number) => {
      const range = scaleMax - scaleMin;
      const meanPct = ((p.mean - scaleMin) / range) * 100;
      const loPct = Math.max(0, ((p.lo - scaleMin) / range) * 100);
      const hiPct = Math.min(100, ((p.hi - scaleMin) / range) * 100);
      const widthPct = Math.max(0, hiPct - loPct);
      return `
        <div class="posterior-card">
          <div class="posterior-head">
            <span class="posterior-label">${escapeHtml(label)}</span>
            <span class="posterior-mean">${p.mean.toFixed(2)}</span>
          </div>
          <div class="posterior-bar">
            <div class="posterior-bar-track"></div>
            <div class="posterior-bar-ci" style="left: ${loPct}%; width: ${widthPct}%;"></div>
            <div class="posterior-bar-mean" style="left: ${meanPct}%;"></div>
            <div class="posterior-bar-scale">
              <span>${scaleMin}</span><span>${scaleMax}</span>
            </div>
          </div>
          <div class="posterior-meta">
            σ ${p.sd.toFixed(2)} · ${p.n_obs} obs · 80% CI ${p.lo.toFixed(2)}–${p.hi.toFixed(2)}
          </div>
        </div>
      `;
    };

    const expEl = result?.topExperiment
      ? `
        <div class="next-experiment">
          <h4>Most informative next experiment</h4>
          <p class="next-experiment-input"><strong>${escapeHtml(READINESS_INPUT_LABELS[result.topExperiment.inputKey] ?? result.topExperiment.inputKey)}</strong></p>
          <p class="next-experiment-rationale">${escapeHtml(result.topExperiment.rationale)}</p>
          <p class="next-experiment-eig muted">EIG ranking · ${result.experiments.slice(0, 3).map((e, i) => `${i + 1}. ${escapeHtml(READINESS_INPUT_LABELS[e.inputKey] ?? e.inputKey)} (${e.eig.toFixed(3)})`).join(" · ")}</p>
        </div>
      `
      : `<div class="next-experiment muted">All inputs already wired — re-run after adding new evidence.</div>`;

    host.innerHTML = `
      <div class="sandbox">
        <aside class="sandbox-rail">
          <header class="sandbox-rail-head"><h3>Topic</h3></header>
          <select id="sandboxTopic" class="sandbox-topic-select">${topicOptions}</select>

          <header class="sandbox-rail-head"><h3>Saved scenarios</h3></header>
          <ul class="scenario-list">${scenarioList}</ul>
          <button class="btn btn-primary" id="sandboxSave">Save current as scenario…</button>
          <button class="btn" id="sandboxReset">Reset shocks + weights</button>
        </aside>

        <section class="sandbox-controls">
          <header class="sandbox-section-head">
            <h3>Macro shocks</h3>
            <span class="muted">−2 to +2 — applied across all sensitive inputs</span>
          </header>
          <div class="shock-grid">${shockSliders}</div>

          <header class="sandbox-section-head">
            <h3>Input weights</h3>
            <span class="muted">0 = drop, 1 = nominal, 2 = high-confidence</span>
          </header>
          <div class="weight-grid">${weightSliders}</div>
        </section>

        <section class="sandbox-output">
          <header class="sandbox-section-head"><h3>Posteriors</h3></header>
          ${result ? posterior("Readiness", result.readiness, 1, 3) : ""}
          ${result ? posterior(`Relevance · ${LENS_LABEL.client}`,     result.relevance.client,     1, 3) : ""}
          ${result ? posterior(`Relevance · ${LENS_LABEL.field}`,      result.relevance.field,      1, 3) : ""}
          ${result ? posterior(`Relevance · ${LENS_LABEL.enterprise}`, result.relevance.enterprise, 1, 3) : ""}
          ${result ? posterior("Timing · years to impact", result.timing_years, 0, 12) : ""}
          ${expEl}
        </section>
      </div>
    `;
    void LENSES;
    bind();
  }

  function bind() {
    host.querySelector<HTMLSelectElement>("#sandboxTopic")?.addEventListener("change", (e) => {
      const id = Number((e.target as HTMLSelectElement).value);
      const next = state.ctxs.find((c) => c.topic.id === id);
      if (next) state.topic = next;
      paint();
    });

    host.querySelectorAll<HTMLInputElement>(".shock-slider-input").forEach((el) => {
      el.addEventListener("input", () => {
        const k = el.dataset.shock as ShockKey;
        state.shocks[k] = Number(el.value);
        // Update only the displayed value + posteriors without a full repaint.
        const valSpan = el.parentElement?.querySelector<HTMLElement>(".shock-slider-value");
        if (valSpan) valSpan.textContent = `${state.shocks[k] >= 0 ? "+" : ""}${state.shocks[k].toFixed(1)}`;
        repaintOutput();
      });
    });

    host.querySelectorAll<HTMLInputElement>(".weight-slider-input").forEach((el) => {
      el.addEventListener("input", () => {
        const k = el.dataset.weight!;
        state.weights[k] = Number(el.value);
        const valSpan = el.parentElement?.querySelector<HTMLElement>(".weight-slider-value");
        if (valSpan) valSpan.textContent = `${state.weights[k].toFixed(1)}×`;
        repaintOutput();
      });
    });

    host.querySelector<HTMLButtonElement>("#sandboxReset")?.addEventListener("click", () => {
      state.shocks = { ...ZERO_SHOCKS };
      state.weights = Object.fromEntries(READINESS_INPUTS.map((k) => [k, 1]));
      paint();
    });

    host.querySelector<HTMLButtonElement>("#sandboxSave")?.addEventListener("click", () => {
      if (!state.topic) return;
      const name = prompt("Name this scenario:");
      if (!name?.trim()) return;
      const thesis = prompt("One-line thesis:") ?? "";
      saveScenario({
        name: name.trim(),
        thesis: thesis.trim(),
        topic_id: state.topic.topic.id,
        topic_name: state.topic.topic.name,
        shocks: { ...state.shocks },
        weights: { ...state.weights },
      });
      state.scenarios = listScenarios();
      paint();
    });

    host.querySelectorAll<HTMLButtonElement>(".scenario-load").forEach((b) => {
      b.addEventListener("click", () => {
        const id = b.dataset.id!;
        const s = state.scenarios.find((sc) => sc.id === id);
        if (!s) return;
        state.shocks = { ...s.shocks };
        state.weights = { ...s.weights };
        const t = state.ctxs.find((c) => c.topic.id === s.topic_id);
        if (t) state.topic = t;
        paint();
      });
    });
    host.querySelectorAll<HTMLButtonElement>(".scenario-del").forEach((b) => {
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        deleteScenario(b.dataset.id!);
        state.scenarios = listScenarios();
        paint();
      });
    });
  }

  /** Lightweight repaint of just the output column + experiment block. */
  function repaintOutput() {
    const out = host.querySelector<HTMLElement>(".sandbox-output");
    if (!out) return paint();
    const result = compute();
    if (!result) return;
    const posterior = (label: string, p: SandboxResult["readiness"], scaleMin: number, scaleMax: number) => {
      const range = scaleMax - scaleMin;
      const meanPct = ((p.mean - scaleMin) / range) * 100;
      const loPct = Math.max(0, ((p.lo - scaleMin) / range) * 100);
      const hiPct = Math.min(100, ((p.hi - scaleMin) / range) * 100);
      const widthPct = Math.max(0, hiPct - loPct);
      return `
        <div class="posterior-card">
          <div class="posterior-head">
            <span class="posterior-label">${escapeHtml(label)}</span>
            <span class="posterior-mean">${p.mean.toFixed(2)}</span>
          </div>
          <div class="posterior-bar">
            <div class="posterior-bar-track"></div>
            <div class="posterior-bar-ci" style="left: ${loPct}%; width: ${widthPct}%;"></div>
            <div class="posterior-bar-mean" style="left: ${meanPct}%;"></div>
            <div class="posterior-bar-scale">
              <span>${scaleMin}</span><span>${scaleMax}</span>
            </div>
          </div>
          <div class="posterior-meta">
            σ ${p.sd.toFixed(2)} · ${p.n_obs} obs · 80% CI ${p.lo.toFixed(2)}–${p.hi.toFixed(2)}
          </div>
        </div>
      `;
    };
    const expEl = result.topExperiment
      ? `
        <div class="next-experiment">
          <h4>Most informative next experiment</h4>
          <p class="next-experiment-input"><strong>${escapeHtml(READINESS_INPUT_LABELS[result.topExperiment.inputKey] ?? result.topExperiment.inputKey)}</strong></p>
          <p class="next-experiment-rationale">${escapeHtml(result.topExperiment.rationale)}</p>
          <p class="next-experiment-eig muted">EIG ranking · ${result.experiments.slice(0, 3).map((e, i) => `${i + 1}. ${escapeHtml(READINESS_INPUT_LABELS[e.inputKey] ?? e.inputKey)} (${e.eig.toFixed(3)})`).join(" · ")}</p>
        </div>
      `
      : `<div class="next-experiment muted">All inputs already wired — re-run after adding new evidence.</div>`;
    out.innerHTML = `
      <header class="sandbox-section-head"><h3>Posteriors</h3></header>
      ${posterior("Readiness", result.readiness, 1, 3)}
      ${posterior(`Relevance · ${LENS_LABEL.client}`,     result.relevance.client,     1, 3)}
      ${posterior(`Relevance · ${LENS_LABEL.field}`,      result.relevance.field,      1, 3)}
      ${posterior(`Relevance · ${LENS_LABEL.enterprise}`, result.relevance.enterprise, 1, 3)}
      ${posterior("Timing · years to impact", result.timing_years, 0, 12)}
      ${expEl}
    `;
  }

  return () => {
    cancelled = true;
    host.innerHTML = "";
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
